/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import { Welcome } from './components/WelcomePage';
import {
  InspectionStatusEnum,
  LenderDocumentTypeEnum,
  SellerPage
} from '@types';
import { License } from './components/License';
import { Vehicle } from './components/Vehicle';
import { Register } from './components/Register';
import { Bank } from './components/Bank';
import { Cert } from './components/Cert';
import { PayoutLetter } from './components/PayoutLetter';
import { ProofOwnership } from './components/ProofOwnership';
import { ReactComponent as LeftIcon } from '@assets/images/left_icon.svg';
import { useOrgStore, useSellerStore, useAuthStore } from '@stores';
import { useNavigate, useParams } from 'react-router-dom';
import { hasRequiredBankStatementFields } from '../Inspections/utils/bankStatementFields';
import _groupBy from 'lodash/groupBy';
import _maxBy from 'lodash/maxBy';
import _flatMap from 'lodash/flatMap';
import {
  GET_INSPECTION,
  GET_INSPECTION_CHECK,
  GET_INSPECTION_FILES,
  GET_LENDER_ORGANISATION,
  CURRENT_USER,
  COMPLETE_SESSION_HANDOVER,
  post
} from '@services';
import _intersection from 'lodash/intersection';
import _indexOf from 'lodash/indexOf';
import Loader from '@components/widgets/Loader';
import { MARINE_PHOTO_LIST, VEHICLE_PHOTO_LIST } from '@constants';
import { isDesktop, isTablet } from 'react-device-detect';
import { recordAPIError } from '@utils/rumMonitoring';

const SESSION_HANDOVER_ERROR_MESSAGES: Record<string, string> = {
  InvalidToken:
    'This inspection link is invalid. Please scan a new QR code from the desktop application.',
  TokenExpired:
    'This QR code has expired. QR codes are valid for 1 hour. Please generate a new QR code from the desktop application and scan it again.',
  AccessDenied:
    'You do not have permission to access this inspection. Please check the link and try again.'
};

const DEFAULT_HANDOVER_ERROR =
  'Something went wrong while connecting to the inspection. Please try scanning the QR code again.';

const SellerHome = () => {
  const [page, setPage] = useState(SellerPage.welcome);
  const {
    activePageTitle,
    driverLicense,
    vinObj,
    marineHin,
    marineRegoNumber,
    marineRegoCert,
    numberPlate,
    odometer,
    vehicleBack,
    vehicleFront,
    vehiclePassenger,
    vehicleDriver,
    vehicleFrontSeats,
    vehicleBackSeats,
    vehicleBoot,
    vehicleLivingArea,
    vehicleKitchen,
    vehicleSleepingArea,
    marineFront,
    marineBack,
    marineLeft,
    marineRight,
    operatingHours,
    registration,
    bank,
    cert,
    payoutLetter,
    ownershipProof,
    setData,
    setVehiclePosition,
    setVinStatus,
    setMarineRegoCertStatus,
    setMarineHinStatus,
    setMarineRegoNumberStatus,
    setNumberPlateStatus,
    setOdometerStatus,
    setOperatingHoursStatus,
    setExternalPhotosStatus,
    setVehicleFrontStatus,
    setVehicleDriverStatus,
    setVehiclePassengerStatus,
    setVehicleBackStatus,
    setVehicleFrontSeatsStatus,
    setVehicleBackSeatsStatus,
    setVehicleBootStatus,
    setVehicleStatus,
    setRegistrationStatus,
    setDriverLicenseStatus,
    setBankStatus,
    setCertStatus,
    setPayoutLetterStatus,
    setOwnershipProofStatus,
    setHasDriverLicense,
    setDriverLicenseAvailableStatus,
    setVinAvailableStatus,
    setMarineHinAvailableStatus,
    setMarineRegoNumberAvailableStatus,
    setMarineRegoCertAvailableStatus,
    setNumberPlateAvailableStatus,
    setOdometerAvailableStatus,
    setOperatingHoursAvailableStatus,
    setCertAvailableStatus,
    setPayoutLetterAvailableStatus,
    setOwnershipProofAvailableStatus,
    setRegistrationAvailableStatus,
    setBankAvailableStatus,
    setMarineFrontAvailableStatus,
    setMarineBackAvailableStatus,
    setMarineLeftAvailableStatus,
    setMarineRightAvailableStatus,
    setVehicleAvailableStatus,
    setExternalPhotosAvailableStatus,
    setVehicleFrontAvailableStatus,
    setVehicleDriverAvailableStatus,
    setVehiclePassengerAvailableStatus,
    setVehicleBackAvailableStatus,
    setVehicleFrontSeatsAvailableStatus,
    setVehicleBackSeatsAvailableStatus,
    setVehicleBootAvailableStatus,
    setVehiclePhotoList,
    reset,
    setVinFile,
    setMarineHinFile,
    setMarineRegoNumberFile,
    setMarineRegoCertFile,
    setPlateNumberFile,
    setOdoFile,
    setOperatingHoursFile,
    setCertFile,
    setPayoutLetterFile,
    setOwnershipProofFile,
    setRegoFile,
    setBankFile,
    setDriverFile,
    setPassengerFile,
    setFrontFile,
    setBackFile,
    setFrontSeatsFile,
    setBackSeatsFile,
    setBootFile,
    setLivingAreaFile,
    setKitchenFile,
    setSleepingAreaFile,
    setVehicleLivingAreaStatus,
    setVehicleKitchenStatus,
    setVehicleSleepingAreaStatus,
    setVehicleLivingAreaAvailableStatus,
    setVehicleKitchenAvailableStatus,
    setVehicleSleepingAreaAvailableStatus,
    setMarineImageFront,
    setMarineImageBack,
    setMarineImageLeft,
    setMarineImageRight,
    setInspectionChecks
  } = useSellerStore();
  const params = useParams();
  const { organisation, setOrg } = useOrgStore();
  const [isLoading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { setUser } = useAuthStore();
  const userId = localStorage.getItem('userId');
  const lenderUser = localStorage.getItem(`lenders_${userId}` as any);
  const getLatestFiles = (files: any) => {
    const groupedFiles = _groupBy(files, 'documentType');

    const latestFiles = _flatMap(groupedFiles, (group) =>
      _maxBy(group, 'createdTime')
    );

    return latestFiles;
  };
  const hasAllDocumentTypes = (files: any, documentTypes: string[]) => {
    if (files.length === 0 || documentTypes.length === 0) {
      return false;
    }
    const foundDocumentTypes = files.map((file: any) => file.documentType);
    return documentTypes.every((type) => foundDocumentTypes.includes(type));
  };
  const navigate = useNavigate();
  const [availableItems, setAvailableItems] = useState<any[]>();
  const urlParams = new URLSearchParams(location.search);
  const email = urlParams.get('email');
  const [isHandoverLoading, setIsHandoverLoading] = useState(false);
  const hash = location.hash.slice(1); // Remove the '#' character

  useEffect(() => {
    reset();
    setErrorMessage(null);
    const fetchData = async () => {
      if (params.id) {
        setLoading(true);
        let lenderOrganisationId = organisation
          ? organisation.organisationId
          : lenderUser && JSON.parse(lenderUser)[0].organisationId;
        if (email && hash) {
          setIsHandoverLoading(true);
          try {
            const res = await post(COMPLETE_SESSION_HANDOVER, {
              sessionToken: hash,
              email
            },
            false // don't use global catch
          );
            localStorage.setItem('accessToken', res.accessToken);
            localStorage.setItem('expiry', res.expiry);
            if (res.clientId) {
              localStorage.setItem('clientId', res.clientId);
            }
          } catch (error: any) {
            const errorCode = error?.response?.data?.error?.code;
            const message =
              SESSION_HANDOVER_ERROR_MESSAGES[errorCode] || DEFAULT_HANDOVER_ERROR;
            setErrorMessage(message);
            setIsHandoverLoading(false);
            return;
          }

          const resp = await post(CURRENT_USER, {}, false);
          if (resp.user) {
            setUser(resp.user);
            localStorage.setItem('userId', resp.user.userId);
            localStorage.setItem('lastLogin', resp.user.lastLogin);
            localStorage.setItem('email', resp.user.email);
            localStorage.setItem('firstName', resp.user.firstName);
            localStorage.setItem('lastName', resp.user.lastName || '');
          }
          const userId = resp.user.userId;
          if (resp.lenders?.length > 0) {
            localStorage.setItem(
              `lenders_${userId}`,
              JSON.stringify(resp.lenders)
            );
            lenderOrganisationId = lenderOrganisationId
              ? lenderOrganisationId
              : resp.lenders[0].organisationId &&
                resp.lenders[0].organisationId;
          }
          if (resp.lenderBrokers && resp.lenderBrokers.length > 0) {
            localStorage.setItem(
              `lenderBrokers_${userId}`,
              JSON.stringify(resp.lenderBrokers)
            );
          }
          if (resp.system) {
            localStorage.setItem(`roles_${userId}`, resp.system.roles);
          }
        }

        post(GET_INSPECTION, { inspectionId: params.id }).then((res) => {
          setLoading(false);
          if (res && res.inspection) {
            setData(res.inspection);
            post(
              GET_LENDER_ORGANISATION,
              { organisationId: res.inspection.lender.organisationId },
              false
            )
              .then((response) => {
                if (response && response.lender) {
                  setOrg(response.lender);
                }
              })
              .catch((e) => {
                recordAPIError(e, e.response?.data);
                console.error(e);
              });
            if (res.inspection.status === InspectionStatusEnum.Completed) {
              navigate(
                '/seller/complete?friendlyId=' + res.inspection.friendlyId
              );
            }
            post(GET_INSPECTION_CHECK, { inspectionId: params.id })
              .then((data) => {
                if (
                  data &&
                  data.inspectionChecks &&
                  Object.keys(data.inspectionChecks).length !== 0
                ) {
                  setInspectionChecks(data.inspectionChecks);
                }
              })
              .catch((e) => {
                recordAPIError(e, e.response?.data);
                console.error(e);
              });
            const validationItems: any[] = [];
            if (
              res.inspection.documentTypes &&
              res.inspection.documentTypes.length > 0
            ) {
              let photoList = VEHICLE_PHOTO_LIST;
              if (res.inspection.assetType === 'MARINE') {
                photoList = MARINE_PHOTO_LIST;
              }
              const verificationPhotos = _intersection(
                photoList,
                res.inspection.documentTypes
              );
              if (verificationPhotos.length > 0) {
                const vehicleIndex = _indexOf(
                  photoList,
                  verificationPhotos[0]
                );
                setVehiclePosition({
                  vehiclePosition: vehicleIndex + 1
                });
              }
              setVehiclePhotoList(verificationPhotos);
            }
            const documentTypeMapping = {
              [LenderDocumentTypeEnum.SELLER_ID]: {
                setAvailable: setDriverLicenseAvailableStatus,
                additionalSet: setHasDriverLicense,
                item: driverLicense
              },
              [LenderDocumentTypeEnum.VIN]: {
                setAvailable: setVinAvailableStatus,
                item: vinObj
              },
              [LenderDocumentTypeEnum.MARINE_HIN]: {
                setAvailable: setMarineHinAvailableStatus,
                item: marineHin
              },
              [LenderDocumentTypeEnum.MARINE_REGO_NUMBER]: {
                setAvailable: setMarineRegoNumberAvailableStatus,
                item: marineRegoNumber
              },
              [LenderDocumentTypeEnum.MARINE_REGO_CERT]: {
                setAvailable: setMarineRegoCertAvailableStatus,
                item: marineRegoCert
              },
              [LenderDocumentTypeEnum.REGO_PLATE]: {
                setAvailable: setNumberPlateAvailableStatus,
                item: numberPlate
              },
              [LenderDocumentTypeEnum.ODOMETER]: {
                setAvailable: setOdometerAvailableStatus,
                item: odometer
              },
              [LenderDocumentTypeEnum.OPERATING_HOURS]: {
                setAvailable: setOperatingHoursAvailableStatus,
                item: operatingHours
              },
              [LenderDocumentTypeEnum.ASSET_BACK]: {
                setAvailable: setVehicleBackAvailableStatus,
                item: vehicleBack
              },
              [LenderDocumentTypeEnum.ASSET_FRONT]: {
                setAvailable: setVehicleFrontAvailableStatus,
                item: vehicleFront
              },
              [LenderDocumentTypeEnum.ASSET_LEFT]: {
                setAvailable: setVehiclePassengerAvailableStatus,
                item: vehiclePassenger
              },
              [LenderDocumentTypeEnum.ASSET_RIGHT]: {
                setAvailable: setVehicleDriverAvailableStatus,
                item: vehicleDriver
              },
              [LenderDocumentTypeEnum.INTERNAL_FRONT]: {
                setAvailable: setVehicleFrontSeatsAvailableStatus,
                item: vehicleFrontSeats
              },
              [LenderDocumentTypeEnum.INTERNAL_BACK]: {
                setAvailable: setVehicleBackSeatsAvailableStatus,
                item: vehicleBackSeats
              },
              [LenderDocumentTypeEnum.INTERNAL_BOOT]: {
                setAvailable: setVehicleBootAvailableStatus,
                item: vehicleBoot
              },

              [LenderDocumentTypeEnum.INTERNAL_LIVING_AREA]: {
                setAvailable: setVehicleLivingAreaAvailableStatus,
                item: vehicleLivingArea
              },
              [LenderDocumentTypeEnum.INTERNAL_KITCHEN]: {
                setAvailable: setVehicleKitchenAvailableStatus,
                item: vehicleKitchen
              },
              [LenderDocumentTypeEnum.INTERNAL_SLEEPING_AREA]: {
                setAvailable: setVehicleSleepingAreaAvailableStatus,
                item: vehicleSleepingArea
              },

              [LenderDocumentTypeEnum.ROADWORTHY_CERT]: {
                setAvailable: setCertAvailableStatus,
                item: cert
              },
              [LenderDocumentTypeEnum.PAYOUT_LETTER]: {
                setAvailable: setPayoutLetterAvailableStatus,
                item: payoutLetter
              },
              [LenderDocumentTypeEnum.OWNERSHIP_PROOF]: {
                setAvailable: setOwnershipProofAvailableStatus,
                item: ownershipProof
              },
              [LenderDocumentTypeEnum.REGO_PAPERS]: {
                setAvailable: setRegistrationAvailableStatus,
                item: registration
              },
              [LenderDocumentTypeEnum.BANK_STATEMENTS]: {
                setAvailable: setBankAvailableStatus,
                item: bank
              },
              [LenderDocumentTypeEnum.MARINE_FRONT]: {
                setAvailable: setMarineFrontAvailableStatus,
                item: null // Will be handled separately
              },
              [LenderDocumentTypeEnum.MARINE_BACK]: {
                setAvailable: setMarineBackAvailableStatus,
                item: null // Will be handled separately
              },
              [LenderDocumentTypeEnum.MARINE_LEFT]: {
                setAvailable: setMarineLeftAvailableStatus,
                item: null // Will be handled separately
              },
              [LenderDocumentTypeEnum.MARINE_RIGHT]: {
                setAvailable: setMarineRightAvailableStatus,
                item: null // Will be handled separately
              }
            };

            const vehicleDocTypes = [
              LenderDocumentTypeEnum.ASSET_FRONT,
              LenderDocumentTypeEnum.ASSET_BACK,

              LenderDocumentTypeEnum.ASSET_LEFT,
              LenderDocumentTypeEnum.ASSET_RIGHT
            ];
            const externalPhotosTypes = [
              LenderDocumentTypeEnum.ODOMETER,
              LenderDocumentTypeEnum.INTERNAL_FRONT,
              LenderDocumentTypeEnum.INTERNAL_BACK,
              LenderDocumentTypeEnum.INTERNAL_BOOT,
              LenderDocumentTypeEnum.INTERNAL_LIVING_AREA,
              LenderDocumentTypeEnum.INTERNAL_KITCHEN,
              LenderDocumentTypeEnum.INTERNAL_SLEEPING_AREA
            ];

            if (res.inspection.documentTypes?.length > 0) {
              // Process regular document types
              Object.entries(documentTypeMapping).forEach(
                ([docType, config]: any) => {
                  if (res.inspection.documentTypes.includes(docType)) {
                    config.setAvailable(true);
                    if (config.additionalSet) {
                      config.additionalSet(true);
                    }
                    validationItems.push(config.item);
                  }
                }
              );

              // Process vehicle documents
              if (
                vehicleDocTypes.some((type) =>
                  res.inspection.documentTypes.includes(type)
                )
              ) {
                setVehicleAvailableStatus(true);
                // validationItems.push(vehicle);
              }
              // Process external photos documents
              if (
                externalPhotosTypes.some((type) =>
                  res.inspection.documentTypes.includes(type)
                )
              ) {
                setExternalPhotosAvailableStatus(true);
                // validationItems.push(externalPhotos);
              }

              // Process marine photos documents
              const marinePhotosTypes = [
                LenderDocumentTypeEnum.MARINE_FRONT,
                LenderDocumentTypeEnum.MARINE_BACK,
                LenderDocumentTypeEnum.MARINE_LEFT,
                LenderDocumentTypeEnum.MARINE_RIGHT
              ];
              
              if (res.inspection.documentTypes.includes(LenderDocumentTypeEnum.MARINE_FRONT)) {
                setMarineImageFront({
                  ...marineFront,
                  available: true
                });
              }
              if (res.inspection.documentTypes.includes(LenderDocumentTypeEnum.MARINE_BACK)) {
                setMarineImageBack({
                  ...marineBack,
                  available: true
                });
              }
              if (res.inspection.documentTypes.includes(LenderDocumentTypeEnum.MARINE_LEFT)) {
                setMarineImageLeft({
                  ...marineLeft,
                  available: true
                });
              }
              if (res.inspection.documentTypes.includes(LenderDocumentTypeEnum.MARINE_RIGHT)) {
                setMarineImageRight({
                  ...marineRight,
                  available: true
                });
              }
            }

            setAvailableItems(validationItems);
            const vehiclePhotos = [
              LenderDocumentTypeEnum.ASSET_BACK,
              LenderDocumentTypeEnum.ASSET_FRONT,
              LenderDocumentTypeEnum.ASSET_LEFT,
              LenderDocumentTypeEnum.ASSET_RIGHT
            ];
            const externalPhotos = [
              LenderDocumentTypeEnum.ODOMETER,
              LenderDocumentTypeEnum.INTERNAL_FRONT,
              LenderDocumentTypeEnum.INTERNAL_BACK,
              LenderDocumentTypeEnum.INTERNAL_BOOT,
              LenderDocumentTypeEnum.INTERNAL_LIVING_AREA,
              LenderDocumentTypeEnum.INTERNAL_KITCHEN,
              LenderDocumentTypeEnum.INTERNAL_SLEEPING_AREA
            ];
            const externalPhotosRequired = _intersection(
              externalPhotos,
              res.inspection.documentTypes
            );
            const vehicleRequired = _intersection(
              vehiclePhotos,
              res.inspection.documentTypes
            );
            if (
              res.inspection?.documents?.ROADWORTHY_CERT?.needsResubmit ===
              false
            ) {
              setCertStatus(true);
            }
            if (
              res.inspection?.documents?.PAYOUT_LETTER?.needsResubmit === false
            ) {
              setPayoutLetterStatus(true);
            }
            if (
              res.inspection?.documents?.OWNERSHIP_PROOF?.needsResubmit ===
              false
            ) {
              setOwnershipProofStatus(true);
            }
            post(GET_INSPECTION_FILES, { inspectionId: params.id }, false)
              .then((data) => {
                if (data && data.files) {
                  const files = getLatestFiles(data.files);
                  if (files.length > 0) {
                    const processFile = (file: any) => {
                      const docs = res.inspection?.documents;
                      switch (file.documentType) {
                        case 'DRIVER_LICENCE_FRONT':
                        case 'DRIVER_LICENCE_BACK':
                        case 'FACE':
                          setDriverLicenseStatus(true);
                          break;
                        case LenderDocumentTypeEnum.VIN:
                          if (docs?.VIN?.fileId && res.inspection?.extraFields['vin'] && !docs?.VIN?.needsResubmit) {
                            setVinStatus(true);
                          }
                          setVinFile(file);
                          break;
                        case LenderDocumentTypeEnum.MARINE_HIN:
                          if (docs?.MARINE_HIN?.fileId && !docs?.MARINE_HIN?.needsResubmit) setMarineHinStatus(true);
                          setMarineHinFile(file);
                          break;
                        case LenderDocumentTypeEnum.MARINE_REGO_NUMBER:
                          if (docs?.MARINE_REGO_NUMBER?.fileId && !docs?.MARINE_REGO_NUMBER?.needsResubmit) setMarineRegoNumberStatus(true);
                          setMarineRegoNumberFile(file);
                          break;
                        case LenderDocumentTypeEnum.MARINE_REGO_CERT:
                          if (docs?.MARINE_REGO_CERT?.fileId && !docs?.MARINE_REGO_CERT?.needsResubmit) setMarineRegoCertStatus(true);
                          setMarineRegoCertFile(file);
                          break;
                        case LenderDocumentTypeEnum.REGO_PLATE:
                          if (docs?.REGO_PLATE?.fileId && res.inspection?.extraFields['licencePlate'] && !docs?.REGO_PLATE?.needsResubmit) setNumberPlateStatus(true);
                          setPlateNumberFile(file);
                          break;
                        case LenderDocumentTypeEnum.OPERATING_HOURS:
                          if (docs?.OPERATING_HOURS?.fileId && !docs?.OPERATING_HOURS?.needsResubmit) setOperatingHoursStatus(true);
                          setOperatingHoursFile(file);
                          break;
                        case LenderDocumentTypeEnum.ODOMETER:
                          if (docs?.ODOMETER?.fileId && !docs?.ODOMETER?.needsResubmit) setOdometerStatus(true);
                          setOdoFile(file);
                          break;
                        case LenderDocumentTypeEnum.ROADWORTHY_CERT:
                          if (!docs?.ROADWORTHY_CERT?.needsResubmit) setCertStatus(true);
                          setCertFile(file);
                          break;
                        case LenderDocumentTypeEnum.PAYOUT_LETTER:
                          if (!docs?.PAYOUT_LETTER?.needsResubmit) setPayoutLetterStatus(true);
                          setPayoutLetterFile(file);
                          break;
                        case LenderDocumentTypeEnum.OWNERSHIP_PROOF:
                          if (!docs?.OWNERSHIP_PROOF?.needsResubmit) setOwnershipProofStatus(true);
                          setOwnershipProofFile(file);
                          break;
                        case LenderDocumentTypeEnum.REGO_PAPERS:
                          if (file.fields2 && res.inspection?.extraFields['regoCertificate'] && !docs?.REGO_PAPERS?.needsResubmit) setRegistrationStatus(true);
                          setRegoFile(file);
                          break;
                        case LenderDocumentTypeEnum.BANK_STATEMENTS:
                          if (
                            file.fields2 &&
                            hasRequiredBankStatementFields(
                              res.inspection?.extraFields?.bankStatement
                            ) &&
                            !docs?.BANK_STATEMENTS?.needsResubmit
                          ) {
                            setBankStatus(true);
                          }
                          setBankFile(file);
                          break;
                        case LenderDocumentTypeEnum.ASSET_FRONT:
                          setFrontFile(file);
                          if (!docs?.ASSET_FRONT?.needsResubmit) setVehicleFrontStatus(true);
                          break;
                        case LenderDocumentTypeEnum.ASSET_BACK:
                          setBackFile(file);
                          if (!docs?.ASSET_BACK?.needsResubmit) setVehicleBackStatus(true);
                          break;
                        case LenderDocumentTypeEnum.INTERNAL_FRONT:
                          setFrontSeatsFile(file);
                          if (!docs?.INTERNAL_FRONT?.needsResubmit) setVehicleFrontSeatsStatus(true);
                          break;
                        case LenderDocumentTypeEnum.INTERNAL_BACK:
                          setBackSeatsFile(file);
                          if (!docs?.INTERNAL_BACK?.needsResubmit) setVehicleBackSeatsStatus(true);
                          break;
                        case LenderDocumentTypeEnum.INTERNAL_BOOT:
                          setBootFile(file);
                          if (!docs?.INTERNAL_BOOT?.needsResubmit) setVehicleBootStatus(true);
                          break;
                        case LenderDocumentTypeEnum.INTERNAL_LIVING_AREA:
                          setLivingAreaFile(file);
                          if (!docs?.INTERNAL_LIVING_AREA?.needsResubmit) setVehicleLivingAreaStatus(true);
                          break;
                        case LenderDocumentTypeEnum.INTERNAL_KITCHEN:
                          setKitchenFile(file);
                          if (!docs?.INTERNAL_KITCHEN?.needsResubmit) setVehicleKitchenStatus(true);
                          break;
                        case LenderDocumentTypeEnum.INTERNAL_SLEEPING_AREA:
                          setSleepingAreaFile(file);
                          if (!docs?.INTERNAL_SLEEPING_AREA?.needsResubmit) setVehicleSleepingAreaStatus(true);
                          break;
                        case LenderDocumentTypeEnum.ASSET_LEFT:
                          setPassengerFile(file);
                          if (!docs?.ASSET_LEFT?.needsResubmit) setVehiclePassengerStatus(true);
                          break;
                        case LenderDocumentTypeEnum.ASSET_RIGHT:
                          setDriverFile(file);
                          if (!docs?.ASSET_RIGHT?.needsResubmit) setVehicleDriverStatus(true);
                          break;
                        case LenderDocumentTypeEnum.MARINE_FRONT:
                          if (!docs?.MARINE_FRONT?.needsResubmit && !docs?.MARINE_FRONT?.isNotProvided) setMarineImageFront({ ...marineFront, isDone: true });
                          break;
                        case LenderDocumentTypeEnum.MARINE_BACK:
                          if (!docs?.MARINE_BACK?.needsResubmit && !docs?.MARINE_BACK?.isNotProvided) setMarineImageBack({ ...marineBack, isDone: true });
                          break;
                        case LenderDocumentTypeEnum.MARINE_LEFT:
                          if (!docs?.MARINE_LEFT?.needsResubmit && !docs?.MARINE_LEFT?.isNotProvided) setMarineImageLeft({ ...marineLeft, isDone: true });
                          break;
                        case LenderDocumentTypeEnum.MARINE_RIGHT:
                          if (!docs?.MARINE_RIGHT?.needsResubmit && !docs?.MARINE_RIGHT?.isNotProvided) setMarineImageRight({ ...marineRight, isDone: true });
                          break;
                      }
                    };

                    files.forEach(processFile);

                    if (res.inspection?.documents?.ROADWORTHY_CERT?.needsResubmit === false) setCertStatus(true);
                    if (res.inspection?.documents?.PAYOUT_LETTER?.needsResubmit === false) setPayoutLetterStatus(true);
                    if (res.inspection?.documents?.OWNERSHIP_PROOF?.needsResubmit === false) setOwnershipProofStatus(true);

                    setVehicleStatus(hasAllDocumentTypes(files, vehicleRequired));
                    setExternalPhotosStatus(hasAllDocumentTypes(files, externalPhotosRequired));
                  }
                }
              })
              .catch((e) => {
                recordAPIError(e, e.response?.data);
                console.error(e);
              });
          }
        });
      }
    };
    fetchData();
  }, [params.id]);

  if (errorMessage) {
    return (
      <div className="px-4 gap-16 flex flex-col justify-center items-center w-full h-screen">
        <div className="mx-auto w-full bg-white gap-6 py-[50px] px-6 rounded-xl shadow-paper relative max-w-[600px]">
          <div className="gap-2 flex flex-col">
            <p className="text-center text-grey-600">{errorMessage}</p>
          </div>
        </div>
      </div>
    );
  }
  if (isLoading)
    return <Loader bgColor="bg-gray-50" bgOpacity="bg-opacity-100" />;
  return (
    <>
      {page !== SellerPage.welcome && (
        <div className={isDesktop ? 'w-[600px] mx-auto' : ''}>
          {!isDesktop ? (
            <div className="border-b bg-white flex items-center w-full h-18 p-4 z-100 absolute">
              <div className="flex items-center justify-between w-full">
                <div
                  className="cursor-pointer flex"
                  onClick={() => {
                    // TODO: a temporary workaround for a frontend issue with the image capture process
                    window.location.reload();
                  }}>
                  <LeftIcon className="mr-2 rotate-180" />
                </div>
                <p className="text-grey-900 font-semibold">{activePageTitle}</p>
                <div className="w-8"></div>
              </div>
            </div>
          ) : (
            <div className="mt-[56px] ">
              <div
                className="cursor-pointer flex"
                onClick={() => {
                  // TODO: a temporary workaround for a frontend issue with the image capture process
                  window.location.reload();
                }}>
                <LeftIcon className="mr-2 rotate-180" />
                <div>Back</div>
              </div>
            </div>
          )}

          <div
            className={`w-full ${
              isTablet ? 'min-height-content-md' : 'min-height-content'
            }  h-full flex flex-col items-start no-scrollbar overflow-y-hidden tablet-portrait:w-[361px] tablet-portrait:mx-auto tablet-portrait:px-0 ${
              !isDesktop ? 'mt-[56px] pt-[12px] px-4' : ''
            }`}>
            {page === SellerPage.license && <License setPage={setPage} />}
            {page === SellerPage.vehicle && <Vehicle setPage={setPage} />}
            {page === SellerPage.registration && <Register setPage={setPage} />}
            {page === SellerPage.bank && <Bank setPage={setPage} />}
            {page === SellerPage.cert && <Cert setPage={setPage} />}
            {page === SellerPage.payoutLetter && (
              <PayoutLetter setPage={setPage} />
            )}
            {page === SellerPage.ownershipProof && (
              <ProofOwnership setPage={setPage} />
            )}
          </div>
        </div>
      )}

      {page === SellerPage.welcome && (
        <div className="container py-8">
          <Welcome setPage={setPage} />{' '}
        </div>
      )}
    </>
  );
};

export default SellerHome;
